import {
  DataGridDateFilter,
  DataGridIssueSubtypeByTypeManagerFilter,
  DataGridIssueTypeByTypeManagerFilter,
  DataGridUserFilter,
  DateWithTime,
  GridHeader,
  type ExtendedGridColDef,
} from '@issuemanager/apps/client/components';
import { IssueType } from '@issuemanager/constants';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalizedIssueTypes } from '../../features';
import { IssueGridSeverityFilter } from '../issue/components/issue-grid/issue-grid-severity-filter';
import { IssueSeverityChip } from '../issue/components/issue-severity-chip';
import {
  ISSUE_TEMPLATE_FIELD_ADDED_BY,
  ISSUE_TEMPLATE_FIELD_CREATED,
  ISSUE_TEMPLATE_FIELD_ISSUE_SUBTYPE,
  ISSUE_TEMPLATE_FIELD_ISSUE_TYPE,
  ISSUE_TEMPLATE_FIELD_SEVERITY,
  ISSUE_TEMPLATE_FIELD_TITLE,
} from './constants';

const commonIssueTemplateGridColumnsConfig = {
  minWidth: 150,
};

export const useIssueTemplateGridColumns = () => {
  const { t } = useTranslation(['settings']);
  const localizedIssueTypes = useLocalizedIssueTypes();

  const column = useCallback(
    (props: ExtendedGridColDef) => ({
      renderHeader: GridHeader,
      ...commonIssueTemplateGridColumnsConfig,
      ...props,
    }),
    [],
  );

  const columns = useMemo(
    () => [
      column({
        field: ISSUE_TEMPLATE_FIELD_TITLE,
        headerName: t('settings:issueTemplates.table.header.title'),
      }),
      column({
        field: ISSUE_TEMPLATE_FIELD_ISSUE_TYPE,
        headerName: t('settings:issueTemplates.table.header.issueType'),
        valueGetter: (params) =>
          localizedIssueTypes[params.value.name as IssueType] ??
          params.value.name,
        renderHeaderFilter: DataGridIssueTypeByTypeManagerFilter,
      }),
      column({
        field: ISSUE_TEMPLATE_FIELD_ISSUE_SUBTYPE,
        headerName: t('settings:issueTemplates.table.header.issueSubtype'),
        valueGetter: (params) => params.value?.name,
        renderHeaderFilter: DataGridIssueSubtypeByTypeManagerFilter,
      }),
      column({
        field: ISSUE_TEMPLATE_FIELD_SEVERITY,
        headerName: t('settings:issueTemplates.table.header.severity'),
        renderCell: (params) => <IssueSeverityChip severity={params.value} />,
        renderHeaderFilter: IssueGridSeverityFilter,
      }),
      column({
        field: ISSUE_TEMPLATE_FIELD_CREATED,
        headerName: t('settings:issueTemplates.table.header.createdAt'),
        renderCell: (params) => <DateWithTime date={params.value} />,
        renderHeaderFilter: DataGridDateFilter,
      }),
      column({
        field: ISSUE_TEMPLATE_FIELD_ADDED_BY,
        headerName: t('settings:issueTemplates.table.header.addedBy'),
        valueGetter: (params) => params.value.name,
        renderHeaderFilter: DataGridUserFilter,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return columns;
};
